'use client';

import { useFollowUser } from '@/hooks/useFollowUser/useFollowUser';
import { useIsFollowing } from '@/hooks/useIsFollowing/useIsFollowing';
import { useProfileHeader } from '@/hooks/useProfileHeader/useProfileHeader';
import { useProfileNavigation } from '@/hooks/useProfileNavigation/useProfileNavigation';
import { useUnlockedList } from '@/hooks/useUnlockedList/useUnlockedList';
import { isPubkyIdentifier } from '@/libs/utils/utils';
import { ProfileUserNotFoundDiscoveryView } from '@/organisms/ProfileUserNotFoundDiscoveryView/ProfileUserNotFoundDiscoveryView';
import { useProfileContext } from '@/providers/ProfileProvider/ProfileProvider';
import { UnlockedListProvider } from '@/providers/UnlockedListProvider/UnlockedListProvider';
import { useAuthStore } from '@/stores/auth/auth.store';
import { ProfilePageLayout } from '../ProfilePageLayout/ProfilePageLayout';

export interface ProfilePageContainerProps {
  /** Child pages to render in the main content area */
  children: React.ReactNode;
}

/**
 * ProfilePageContainer - Smart component that handles business logic for profile pages
 *
 * This container is responsible for:
 * - Reading profile context (pubky, isOwnProfile)
 * - Data fetching (profile, stats)
 * - Navigation state (active pages, routing)
 * - Action handlers (edit, copy, sign out, follow, etc.)
 * - Handling user not found state
 *
 * It delegates presentation concerns to ProfilePageLayout, which is a dumb component
 * that only receives props and renders UI.
 *
 * This separation follows the container/presentation pattern:
 * - Container (this): Smart, handles logic, connects to stores/hooks
 * - Presentation (ProfilePageLayout): Dumb, receives props, renders UI
 *
 * @example
 * ```tsx
 * // In app/profile/layout.tsx
 * export default function ProfileLayout({ children }) {
 *   return (
 *     <ProfileProvider>
 *       <ProfilePageContainer>
 *         {children}
 *       </ProfilePageContainer>
 *     </ProfileProvider>
 *   );
 * }
 * ```
 */
export function ProfilePageContainer({ children }: ProfilePageContainerProps) {
  // Business logic: Get profile context (pubky and isOwnProfile)
  const { pubky, isOwnProfile, isLoading: isProfileContextLoading } = useProfileContext();

  // Check if logout is in progress (global state to prevent flash of weird states)
  const isLoggingOut = useAuthStore((state) => state.isLoggingOut);

  // Business logic: Fetch profile data and stats
  // Note: useProfileHeader guarantees a non-null profile with default values during loading
  const { profile, stats, actions, isLoading, isProfileLoading, userNotFound } = useProfileHeader(pubky ?? '', {
    enabled: pubky !== null && isPubkyIdentifier(pubky),
  });

  // Read here, not in the sidebar: this layout survives profile tab navigation, so the enumeration
  // runs once per visit and the Unlocked screen reuses it through `UnlockedListProvider`.
  const unlocked = useUnlockedList({ enabled: isOwnProfile });

  // Business logic: Handle navigation state
  const { activePage, filterBarActivePage, navigateToPage } = useProfileNavigation();

  // Business logic: Handle follow/unfollow for other users' profiles (with auth check)
  const { toggleFollow, isLoading: isFollowLoading, loadingAction: followLoadingAction } = useFollowUser();
  const { isFollowing } = useIsFollowing(pubky !== null && isPubkyIdentifier(pubky) ? pubky : '');

  const handleFollowToggle = () => {
    if (!pubky) return;
    void toggleFollow(pubky, isFollowing);
  };

  const mergedActions = {
    ...actions,
    onFollowToggle: handleFollowToggle,
    isFollowLoading,
    followLoadingAction,
    isFollowing,
  };

  const showUserNotFoundDiscovery =
    (pubky !== null && pubky !== '' && !isPubkyIdentifier(pubky)) ||
    (userNotFound && !isOwnProfile && !isLoggingOut && !isProfileContextLoading);

  // `null` on error, not `undefined`: the read has finished, so a spinner would be wrong, and `0`
  // would claim nothing is unlocked.
  // A failed refresh hides the count only when there is nothing to count; the Unlocked screen keeps
  // showing the cached items, so the sidebar shows their number.
  const unlockedCount =
    unlocked.isError && unlocked.count === 0 ? null : unlocked.isLoading ? undefined : unlocked.count;

  if (showUserNotFoundDiscovery) {
    return <ProfileUserNotFoundDiscoveryView />;
  }

  // Delegate presentation to layout organism
  return (
    <UnlockedListProvider value={unlocked}>
      <ProfilePageLayout
        profile={profile}
        stats={stats}
        unlockedCount={unlockedCount}
        actions={mergedActions}
        activePage={activePage}
        filterBarActivePage={filterBarActivePage}
        navigateToPage={navigateToPage}
        isLoading={isLoading}
        isHeaderLoading={isProfileLoading}
        isOwnProfile={isOwnProfile}
        userId={pubky ?? ''}
      >
        {children}
      </ProfilePageLayout>
    </UnlockedListProvider>
  );
}
